import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Eye, EyeSlash, Check } from '@phosphor-icons/react';
import axios from 'axios';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('La password deve avere almeno 6 caratteri'); return; }
    if (password !== confirm) { setError('Le password non corrispondono'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel reset della password');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-6">
        <div className="bg-white rounded-sm p-8 max-w-md text-center border border-[#E4E4E7]">
          <p className="text-red-600 font-medium mb-4">Link non valido. Richiedi un nuovo reset dalla pagina di login.</p>
          <Button onClick={() => navigate('/login')} className="bg-[#2D2A4A] rounded-sm">Vai al Login</Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-6">
        <div className="bg-white rounded-sm p-8 max-w-md text-center border border-[#E4E4E7]" data-testid="reset-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check size={32} className="text-green-600" /></div>
          <h2 className="text-2xl font-bold font-['Work_Sans'] mb-2">Password Reimpostata!</h2>
          <p className="text-[#52525B] mb-6">Ora puoi accedere con la nuova password.</p>
          <Button onClick={() => navigate('/login')} className="bg-[#2D2A4A] rounded-sm">Vai al Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-6">
      <div className="bg-white rounded-sm p-8 max-w-md w-full border border-[#E4E4E7]" data-testid="reset-form">
        <button onClick={() => navigate('/login')} className="flex items-center gap-1 text-sm text-[#52525B] hover:text-[#0A0A0A] mb-6"><ArrowLeft size={16} />Torna al Login</button>
        <h2 className="text-2xl font-bold font-['Work_Sans'] mb-2">Nuova Password</h2>
        <p className="text-[#52525B] text-sm mb-6">Inserisci la tua nuova password</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="label-overline">Nuova Password</Label>
            <div className="relative">
              <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder="Min. 6 caratteri" className="h-12 rounded-sm pr-12" data-testid="reset-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B]">{showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}</button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-overline">Conferma Password</Label>
            <Input type={showPassword ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} required placeholder="Ripeti la password" className="h-12 rounded-sm" data-testid="reset-confirm-input" />
          </div>
          {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-sm text-sm">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full h-12 bg-[#2D2A4A] rounded-sm text-base" data-testid="reset-submit">{loading ? 'Reimpostazione...' : 'Reimposta Password'}</Button>
        </form>
      </div>
    </div>
  );
}
